import React, { useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Users, Activity, Stethoscope, FlaskConical, Scan, Pill, Settings, LogOut, Menu, X, User as UserIcon, FileText, Clock, Baby, CreditCard } from 'lucide-react';
export const DashboardLayout: React.FC = () => {
  const {
    currentUser,
    logout
  } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-23" data-mp="wzu8vdhilx0" />;
  }
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const getNavItems = () => {
    const items = [];
    if (currentUser.role === 'Administrator') {
      items.push({
        name: 'System Overview',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
      items.push({
        name: 'Billing & Invoicing',
        path: '/dashboard/billing',
        icon: CreditCard
      });
      items.push({
        name: 'Pharmacy Inventory',
        path: '/dashboard/pharmacy',
        icon: Pill
      });
      items.push({
        name: 'Staff Management',
        path: '/dashboard/staff',
        icon: Settings
      });
      items.push({
        name: 'Activity Log',
        path: '/dashboard/activity-log',
        icon: Clock
      });
    } else if (currentUser.role === 'Clinician') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else if (currentUser.role === 'Nursing') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      if (currentUser.role === 'Pharmacy' || currentUser.role === 'Pharmacy Manager') {
        items.push({
          name: currentUser.role === 'Pharmacy Manager' ? 'Pharmacist' : 'Pharmacy Dispensing',
          path: '/dashboard/pharmacy',
          icon: Pill
        });
      }
      if (currentUser.role === 'Reception') {
        items.push({
          name: 'Billing & Invoicing',
          path: '/dashboard/billing',
          icon: CreditCard
        });
      }
    }
    return items;
  };
  const navItems = getNavItems();
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Administrator':
        return 'bg-red-100 text-red-800';
      case 'Clinician':
        return 'bg-blue-100 text-blue-800';
      case 'Nursing':
        return 'bg-green-100 text-green-800';
      case 'Laboratory':
        return 'bg-yellow-100 text-yellow-800';
      case 'Radiology':
        return 'bg-purple-100 text-purple-800';
      case 'Pharmacy':
      case 'Pharmacy Manager':
        return 'bg-teal-100 text-teal-800';
      case 'Reception':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="min-h-screen bg-gray-100 flex" data-id="element-24" data-mp="49k9q8759ng7">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 flex md:hidden" data-id="element-25" data-mp="2tz5j7tunnobs">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-26" data-mp="2tw9ijxrfaljn"></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-[#1B3A61]" data-id="element-27" data-mp="gql4h4p2stu5">
            <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-28" data-mp="li34nqj9yqe2">
              <button className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-29" data-mp="lje7no4wbu44">
                <X className="h-6 w-6 text-white" data-id="element-30" data-mp="ziducq8ulyxn" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto" data-id="element-31" data-mp="14gnsn9pvdfnh">
              <div className="flex-shrink-0 flex items-center px-4" data-id="element-32" data-mp="qgmonleb5s3s">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-33" data-mp="qgczu1501qzo" />
                <div className="ml-2" data-id="element-34" data-mp="3g685js45ev9e">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-35" data-mp="3g79hrzwwhnsp">
                    BMED Medical Centre
                  </span>
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-300" data-id="element-36" data-mp="33o46ccz9c7qq">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-1" data-id="element-37" data-mp="3cniv97zs0a94">
                {navItems.map(item => {
              const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
              return <a key={item.name} href={item.path} onClick={e => {
                e.preventDefault();
                navigate(item.path);
                setSidebarOpen(false);
              }} className={`${isActive ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'} group flex items-center px-3 py-2 text-base font-medium rounded-md`} data-id="element-38" data-mp="2yp1c7nktflxj">
                      <item.icon className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'} mr-4 flex-shrink-0 h-6 w-6`} data-id="element-39" data-mp="a391my29s0p2" />
                      {item.name}
                    </a>;
            })}
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-white/10 p-4" data-id="element-40" data-mp="3ielss7tqjko1">
              <div className="flex items-center" data-id="element-41" data-mp="3nbvou6bmquku">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10" data-id="element-42" data-mp="3rd769okco9hy">
                  <UserIcon className="h-6 w-6 text-slate-200" data-id="element-43" data-mp="75rp616exrk" />
                </div>
                <div className="ml-3" data-id="element-44" data-mp="16mq6cunoe8d8">
                  <p className="text-base font-medium text-white" data-id="element-45" data-mp="16looo7c5mnmj">
                    {currentUser.fullName}
                  </p>
                  <p className="text-sm font-medium text-slate-300" data-id="element-46" data-mp="1bnw4j803unu5">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400" data-id="element-47" data-mp="1gry8u9crycv6">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 print:hidden" data-id="element-48" data-mp="17xjwaejmpgfr">
        <div className="flex flex-col w-64" data-id="element-49" data-mp="12zru8rp9sdxu">
          <div className="flex-1 flex flex-col min-h-0 bg-[#1B3A61]" data-id="element-50" data-mp="130jqm4qoyfp6">
            <div className="flex-1 flex flex-col overflow-y-auto pb-4" data-id="element-51" data-mp="xz40vzdkjl0g">
              <div className="flex flex-shrink-0 items-center border-b border-white/10 px-4 py-5" data-id="element-52" data-mp="t0j1u3a1z7dl">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-53" data-mp="ozz5v317u9t1" />
                <div className="ml-3" data-id="element-54" data-mp="2jbduthmek57q">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-55" data-mp="32lqa1il94n8f">
                    BMED Medical Centre
                  </span>
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-300" data-id="element-56" data-mp="36n24q2izfynh">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-6 flex-1 space-y-1 px-3" data-id="element-57" data-mp="2xtysfxk2pruf">
                {navItems.map(item => {
                const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
                return <a key={item.name} href={item.path} onClick={e => {
                  e.preventDefault();
                  navigate(item.path);
                }} className={`${isActive ? 'bg-white/15 text-white' : 'text-slate-300 hover:bg-white/10 hover:text-white'} group flex items-center rounded-md px-3 py-2.5 text-sm font-medium`} data-id="element-58" data-mp="2ewnuqhe3b6h3">
                      <item.icon className={`${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'} mr-3 flex-shrink-0 h-5 w-5`} data-id="element-59" data-mp="ibzczph4upjl" />
                      {item.name}
                    </a>;
              })}
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-white/10 p-4" data-id="element-60" data-mp="161jz2h8l22x3">
              <div className="flex items-center" data-id="element-61" data-mp="110e4edvd45kx">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10" data-id="element-62" data-mp="1f0x7r7ovungr">
                  <UserIcon className="h-5 w-5 text-slate-200" data-id="element-63" data-mp="1ayaf54ke0yii" />
                </div>
                <div className="ml-3" data-id="element-64" data-mp="3grifihhlupkf">
                  <p className="text-sm font-medium text-white" data-id="element-65" data-mp="3gsjtatq09q5u">
                    {currentUser.fullName}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-slate-300" data-id="element-66" data-mp="3cs9ovlhv37t3">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400" data-id="element-67" data-mp="2co27nvsm3ou1">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden" data-id="element-68" data-mp="22t0ix1290orz">
        <header className="flex items-center justify-between gap-3 border-b border-gray-200 bg-white px-3 py-2 sm:px-6 print:hidden" data-id="element-69" data-mp="30fheo1vc2ubm">
          <button className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 md:hidden" onClick={() => setSidebarOpen(true)} data-id="element-70" data-mp="2vgwo6u5vvp0d">
            <span className="sr-only" data-id="element-71" data-mp="3j6qwcyf9cpea">Open sidebar</span>
            <Menu className="h-6 w-6" data-id="element-72" data-mp="3eez84ujkqvrb" />
          </button>
          <div className="ml-auto flex items-center gap-3" data-id="element-73" data-mp="3edeed7533gui">
            <div className="hidden text-right sm:block" data-id="element-74" data-mp="3lqnelupv0uuy">
              <p className="text-sm font-medium leading-tight text-gray-900" data-id="element-75" data-mp="3ls895kc6cdst">
                {currentUser.fullName}
              </p>
              <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(currentUser.role)}`} data-id="element-76" data-mp="3vns1jwrj4uct">
                {currentUser.role}
              </span>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs font-medium sm:hidden ${getRoleColor(currentUser.role)}`} data-id="element-77" data-mp="8j2ddlz207vo">
              {currentUser.role}
            </span>
            <button onClick={handleLogout} className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-78" data-mp="i9x1ywzrvjka">
              <LogOut className="mr-2 h-4 w-4" data-id="element-79" data-mp="w9fx8p7hj0f6" />
              Sign out
            </button>
          </div>
        </header>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none" data-id="element-80" data-mp="2t7fzjclclerk">
          <div className="py-6" data-id="element-81" data-mp="2o8mmtmdvxrsg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-82" data-mp="321vunim6q58v">
              <Outlet data-id="element-83" data-mp="3248l6k60dnxj" />
            </div>
          </div>
        </main>
      </div>
    </div>;
};